import { Injectable, inject } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { SocketService } from '../../../../core/services/socket.service';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { Server } from 'src/app/shared/models/server.model';
import { IpreloadWithType } from '../../../systems/services/password.service';

/**
 * servers service
 * @class ServersService
 */
@Injectable()
export class ServersService {

  /**
   * Injected HttpClient service
   * @type {HttpClient}
   */
  private http = inject(HttpClient);

  /**
   * Injected SocketService service
   * @type {SocketService}
   */
  private socketService = inject(SocketService);


  /**
   * Base API URL
   * @type {string}
   */
  private baseApiUrl = environment.apiUrl + 'company/update-server';

  /**
   * Echo instance for WebSocket connection
   * @type {any}
   */
  private echoInstance = null;

  /**
   * Subject for server events
   * @type {Subject<IserverWithType>}
   */
  private socketServers$: Subject<IserverWithType> = new Subject();

  /**
   * Starts listening to server update events via WebSocket
   * @returns {void}
   */
  startServerSocket() {
    this.echoInstance = this.socketService.getEchoInstance();
    this.echoInstance.private(`company.${this.socketService.companyId}.update-server`)
      .listen('.UpdateServerAddedEvent', (event) => {
        this.socketServers$.next({ action: 'add', server: event.server });
      })
      .listen('.UpdateServerDeletedEvent', (event) => {
        this.socketServers$.next({ action: 'delete', server: event.server });
      })
      .listen('.UpdateServerUpdatedEvent', (event) => {
        this.socketServers$.next({ action: 'update', server: event.server });
      });
  }

  /**
   * subscribes to server socket
   * @returns {Observable<IpreloadWithType>}
   */
  subSocketServer(): Observable<IpreloadWithType> {
    return this.socketServers$;
  }

  /**
   * stop server socket
   * @returns {void}
   */
  stopServerSocket() {
    if (this.echoInstance) {
      this.echoInstance.leave(`company.${this.socketService.companyId}.update-server`);
    }
  }

  /**
   * fetches servers with filters
   * @param filters
   * @returns {Promise<any>}
   */
  getServers(filters): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl, { params: filters }));
  }

  /**
   * update server
   * @param data
   * @returns {Promise<any>}
   */
  updateServer(data): Promise<any> {
    return firstValueFrom(this.http.put(this.baseApiUrl, data));
  }

  /**
   * deletes a server
   * @param data
   * @returns {Promise<any>}
   */
  deleteServer(data): Promise<any> {
    return firstValueFrom(this.http.request('delete', this.baseApiUrl, { body: data }));
  }

  /**
   * adds server
   * @param data
   * @returns {Promise<any>}
   */
  addServer(data): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl, data));
  }
}

/**
 * interface for server with type
 * @interface IserverWithType
 */
export interface IserverWithType {
  /**
   * the server object
   * @type {Server}
   */
  server: Server;
  /**
   * type of action
   * @type {'add' | 'update' | 'delete'}
   */
  action: 'add' | 'update' | 'delete';
}
